// TODO: Preload this in the head tag
import { Users } from '@platform/client';
import { captureException } from '@sentry/react';
import mixpanel, { Mixpanel } from 'mixpanel-browser';
import { useCallback, useEffect, useState } from 'react';

export const MIXPANEL_ENABLED =
  !import.meta.env.SSR &&
  !!import.meta.env.VITE_MIXPANEL_TOKEN?.length &&
  (import.meta.env.VITE_ENVIRONMENT === 'development' || window?.CookieConsent?.statistics);

type MixpanelProperties = Parameters<Mixpanel['track']>[1];

/**
 *  https://docs.mixpanel.com/docs/tracking-methods/choosing-the-right-method#client-side-tracking
 */

export const initMixpanel = () => {
  mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
    debug: import.meta.env.VITE_ENVIRONMENT === 'development',
    track_pageview: 'url-with-path-and-query-string',
    persistence: 'localStorage',
  });

  if (!MIXPANEL_ENABLED) {
    try {
      // Oddly, this function seems to consistently crash when Mixpanel is disabled. It's not clear that this works,
      // os manual checks to MIXPANEL_ENABLED have been added elsewhere in this file
      mixpanel.opt_out_tracking();
    } catch (e) {
      captureException(e);
    }
    return;
  }

  mixpanel.register({
    // Indicates that the user is using this application (the new "Platform" app) instead of
    // the legacy Hapi app
    from_new_platform: true,
    // TODO: App version?
  });
};

/** Gets the current signed-in user and registers them with mixpanel analytics */
export const useIdentifyMixpanelUser = () => {
  const userData = Users.useGetCurrentUser();

  const [isUserIdentified, setIsUserIdentified] = useState(false);

  useEffect(() => {
    if (!MIXPANEL_ENABLED) {
      return;
    }

    if (!isUserIdentified && userData.isSuccess && userData.data) {
      // TODO: Look more closely at analyticsIdentify() in the old app
      mixpanel.identify(userData.data.userId.toString());
      setIsUserIdentified(true);
    }
  }, [isUserIdentified, userData.data, userData.isSuccess]);
};

/**
 * Attaches a Mixpanel track_links() call for a specific event to an element via the returned ref. The track_links
 * will fire when the element is clicked
 * @param eventName String name of the event. If the empty string this function does not attach the listener and just returns the (useless) ref. This is effectively a no-op for cases where we don't need tracking.
 * @param properties Optional properties to attach to the event. The Mixpanel docs claim this can be a function that returns properties when the link is clicked
 * @returns The ref to attach to the element that should trigger tracking
 */
export const useMixpanelTrackLink = <T extends HTMLElement = HTMLDivElement>(
  eventName: string,
  properties?: MixpanelProperties,
) => {
  const setRef = useCallback(
    (el: T) => {
      if (!MIXPANEL_ENABLED) {
        return;
      }

      if (eventName.length && el) {
        mixpanel.track_links(el, eventName, properties);
      }
    },
    [eventName, properties],
  );

  return setRef;
};
